import { useState } from 'react';
import type { Snapshot } from '@proxmon/shared';
import { relativeTime } from '../format.ts';
import { useStore } from '../state/store.ts';
import { SettingsDialog } from './SettingsDialog.tsx';
import { UsersDialog } from './UsersDialog.tsx';

export function TopBar({ snapshot }: { snapshot: Snapshot | null }) {
  const session = useStore((s) => s.session);
  const search = useStore((s) => s.search);
  const setSearch = useStore((s) => s.setSearch);
  const connected = useStore((s) => s.connected);
  const logout = useStore((s) => s.logout);
  const requestFit = useStore((s) => s.requestFit);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);

  const isAdmin = session?.role === 'admin';
  const isViewer = session?.role === 'viewer';

  return (
    <header className="topbar">
      <div className="brand">
        {session?.appName ?? 'Proxmon'}
        <span className="cluster">
          {snapshot?.clusterName ?? (snapshot?.standalone ? 'standalone node' : '—')}
        </span>
      </div>

      <EndpointPills snapshot={snapshot} />

      <div className="spacer" />

      <div className="search">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name, vmid, tag, MAC, IP…"
          aria-label="Search the map"
          spellCheck={false}
        />
        {search && (
          <button type="button" className="clear" onClick={() => setSearch('')} aria-label="Clear search">
            ×
          </button>
        )}
      </div>

      <button type="button" className="btn ghost" onClick={requestFit} title="Fit the whole cluster in view (f)">
        Fit
      </button>

      {snapshot && (
        <span
          className="endpoint"
          title={connected ? 'Receiving live updates' : 'Live updates interrupted; showing the last data received'}
        >
          <span className="dot" style={{ background: connected ? 'var(--ok)' : 'var(--warn)' }} />
          {relativeTime(snapshot.generatedAt)}
        </span>
      )}

      {isAdmin && (
        <>
          <button type="button" className="btn" onClick={() => setUsersOpen(true)} title="Who may sign in">
            Users
          </button>
          <button type="button" className="btn" onClick={() => setSettingsOpen(true)}>
            Settings
          </button>
        </>
      )}

      {isViewer && (
        <span className="pill idle" title="Signed in with read-only rights">
          read-only
        </span>
      )}

      <span className="who" title={session?.username ?? undefined}>
        {session?.username}
      </span>
      <button type="button" className="btn ghost" onClick={() => void logout()}>
        Sign out
      </button>

      {settingsOpen && <SettingsDialog onClose={() => setSettingsOpen(false)} />}
      {usersOpen && <UsersDialog onClose={() => setUsersOpen(false)} />}
    </header>
  );
}

function EndpointPills({ snapshot }: { snapshot: Snapshot | null }) {
  if (!snapshot || snapshot.endpoints.length === 0) return null;

  return (
    <div className="endpoints" role="group" aria-label="Proxmox API endpoints">
      {snapshot.endpoints.map((endpoint) => (
        <span
          key={endpoint.id}
          className={`endpoint ${endpoint.state}${endpoint.active ? ' active' : ''}`}
          title={
            endpoint.state === 'down'
              ? `${endpoint.target} — down: ${endpoint.lastError ?? 'unknown error'} (last OK ${relativeTime(endpoint.lastOkAt)})`
              : `${endpoint.target} — ${endpoint.active ? 'serving polls' : 'standby'}${
                  endpoint.latencyMs !== null ? `, ${endpoint.latencyMs}ms` : ''
                }`
          }
        >
          <span className="dot" />
          {endpoint.label}
        </span>
      ))}
    </div>
  );
}
